import { format } from 'date-fns';
import { User } from 'src/models/user/_types';
import { Identifier, Timestamp } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { decamelize } from 'src/utils/decamelize';
import { decamelizeKeys } from 'src/utils/decamelize-keys';
import { removeReadOnlyFields } from 'src/utils/fieldNormalizers';
import { isEmpty } from 'src/utils/typeCheck';

import { Group } from '../group/_types';
import { normalize } from '../shared/normalizers';
import { normalizeUser } from '../user/normalizers';

import {
  Assessment,
  AssessmentImportParams,
  AssessmentReminder,
  AssessmentSequence,
  GroupAssessment,
  GroupingItem,
  UserAssessment,
} from './_types';
import {
  denormalizeAssessment,
  normalizeAssessment,
  normalizeAssessmentSequence,
  normalizeAssessmentSequenceList,
  normalizeUserAssessment,
} from './normalizers';

interface AssessmentListItem {
  id: number;
  name: string;
}

export const fetchAssessmentOptions = async (params = {}): Promise<
  AssessmentListItem[]
> => {
  const response = await api.get<AssessmentListItem[]>('/api/v2/assessments', {
    params: {
      fetch_all: true,
      pluck_columns: 'id,name',
      'q[s]': 'name asc',
      ...params
    },
  });

  return response.data;
};

export const fetchGroupAssessmentsByGroupId = async ({
  groupId,
  search,
  tags,
  sortBy,
  sortDir,
  page,
  perPage,
}: {
  groupId: Group['id'];
  search?: string;
  tags?: string[];
  sortBy?: string;
  sortDir?: string;
  page?: number;
  perPage?: number;
}): Promise<{ groupAssessments: GroupAssessment[]; total: number }> => {
  const response = await api.get(`/api/v3/group_assessments`, {
    params: {
      group_id: groupId,
      'q[tags_name_eq_any]': tags,
      'q[assessment_name_cont]': search,
      'q[s]': sortBy && sortDir && `${decamelize(sortBy || '')} ${sortDir}`,
      page,
      per: perPage,
    },
  });

  let normalizedAssessments = response.data?.group_assessment?.map(
    (groupAssessment) => normalize(groupAssessment)
  );

  const assessmentIds = normalizedAssessments.map(
    (assessment) => assessment.assessmentId
  );
  const assessments = assessmentIds.length
    ? await fetchAssessmentByIds({
      assessmentIds,
    })
    : [];

  normalizedAssessments = normalizedAssessments.map((assessment) => {
    const as = assessments.find((a) => a.id === assessment.assessmentId);
    return { ...assessment, name: as?.name || assessment.name };
  });
  return {
    groupAssessments: normalizedAssessments,
    total: response.data?.meta?.total,
  };
};

export const fetchGroupAssessment = async ({
  groupAssessmentId,
}: {
  groupAssessmentId: GroupAssessment['id'];
}): Promise<GroupAssessment> => {
  const response = await api.get(
    `/api/v3/group_assessments/${groupAssessmentId}`
  );
  return normalize(response.data);
};

export const newGroupAssessment = async ({
  data,
}: {
  data: Partial<GroupAssessment>;
}) => {
  const payload = decamelizeKeys(data, { deep: true });
  removeReadOnlyFields(payload, ['tags']);
  const response = await api.post(`/api/v3/group_assessments`, {
    group_assessment: {
      ...payload,
      tag_list: data.tags?.join(','),
    },
  });
  return normalize(response.data);
};

export const updateGroupAssessment = async ({
  groupAssessmentId,
  data,
}: {
  groupAssessmentId: GroupAssessment['id'];
  data: Partial<GroupAssessment>;
}) => {
  const payload = decamelizeKeys(data, { deep: true });
  removeReadOnlyFields(payload, ['tags']);
  const response = await api.put(
    `/api/v3/group_assessments/${groupAssessmentId}`,
    {
      group_assessment: {
        ...payload,
        tag_list: data.tags?.join(','),
      },
    }
  );
  return normalize(response.data);
};

export const deleteGroupAssessment = async ({
  groupAssessmentId,
}: {
  groupAssessmentId: GroupAssessment['id'];
}) => {
  await api.delete(`/api/v3/group_assessments/${groupAssessmentId}`);
};

export const fetchAssessmentByIds = async ({
  assessmentIds,
}: {
  assessmentIds: GroupAssessment['assessmentId'][];
}): Promise<Assessment[]> => {
  const response = await api.get<Assessment[]>(`/api/v2/assessments`, {
    params: {
      with_ids: assessmentIds.join(','),
    },
  });

  return response.data.map(normalizeAssessment);
};

interface FetchAssignedAssessmentsOptions {
  userId: User['id'];
}

export const fetchAssignedAssessments = async ({
  userId,
}: FetchAssignedAssessmentsOptions): Promise<Assessment[]> => {
  const response = await api.get<Assessment[]>('/api/v2/assessments', {
    params: {
      user_id: userId,
      assigned: true,
      fetch_all: true,
    },
  });

  return response.data.map(normalizeAssessment);
};

interface FetchAssignedAssessmentSequencesResponse {
  grouping: AssessmentSequence[];
  meta: {
    total: number;
  };
}

export const fetchAssignedAssessmentSequences = async (
  userId: User['id']
): Promise<AssessmentSequence[]> => {
  const response = await api.get<FetchAssignedAssessmentSequencesResponse>(
    '/api/v3/groupings',
    {
      params: {
        user_id: userId,
        assigned: true,
      },
    }
  );

  // Normalize the response
  return response.data.grouping.map(normalizeAssessmentSequence);
};

export interface FetchUserAssessmentsResponse {
  assessment: UserAssessment[];
  meta: {
    total: number;
  };
}

interface FetchUserAssessmentsOptions {
  userId?: User['id'];
  page?: number;
  perPage?: number;
  searchTerm?: string;
  lead?: boolean;
  forTrainer?: boolean;
  withUsers?: boolean;
  withAssessments?: boolean;
  userIdNotNull?: boolean;
  assessmentId?: Identifier;
  locationId?: Identifier;
  userFirstName?: string;
  userLastName?: string;
  userEmail?: string;
  userIdEq?: User['id'];
}

export const fetchUserAssessments = async ({
  userId,
  page = 1,
  perPage = 10,
  searchTerm = '',
  lead,
  forTrainer,
  withUsers = false,
  withAssessments = false,
  userIdNotNull,
  assessmentId,
  locationId,
  userFirstName,
  userLastName,
  userEmail,
  userIdEq,
}: FetchUserAssessmentsOptions): Promise<{
  userAssessments: UserAssessment[];
  total: number;
}> => {
  const params: Record<string, any> = {
    user_id: userId,
    page,
    per: perPage,
    for_trainer: forTrainer,
    'q[assessment_name_cont]': searchTerm,
    'q[lead_true]': lead,
    'q[user_id_eq]': userIdEq,
  };

  if (userIdNotNull) {
    params['q[user_id_not_null]'] = true;
  }
  if (assessmentId) {
    params['q[filter_asessment_template]'] = assessmentId;
  }
  if (locationId) {
    params['q[user_location_id_eq]'] = locationId;
  }

  if (userFirstName && !userLastName && !userEmail) {
    params['q[user_first_name_or_user_last_name_or_user_email_cont]'] =
      userFirstName;
  } else {
    if (userFirstName) {
      params['q[user_first_name_cont]'] = userFirstName;
    }
    if (userLastName) {
      params['q[user_last_name_cont]'] = userLastName;
    }
    if (userEmail) {
      params['q[user_email_cont]'] = userEmail;
    }
  }

  const response = await api.get<FetchUserAssessmentsResponse>(
    '/api/v2/user_assessments',
    {
      params,
    }
  );

  const userAssessments = response.data.assessment.map(normalizeUserAssessment);

  const assessmentIds = userAssessments
    .map((assessment) => assessment.assessmentId)
    .filter((v) => !isEmpty(v));
  if (withAssessments && assessmentIds.length > 0) {
    const response = await api.get<Assessment[]>('/api/v2/assessments/', {
      params: {
        with_ids: Array.from(new Set(assessmentIds)).join(','),
      },
    });

    const assessments = response.data.map(normalizeAssessment);

    userAssessments.forEach((assessment) => {
      assessment.assessment = assessments.find(
        (a) => a.id === assessment.assessmentId
      );
    });
  }

  const userIds = userAssessments
    .map((assessment) => assessment.userId)
    .filter((v) => !isEmpty(v));
  if (withUsers && userIds.length > 0) {
    const response = await api.get<User[]>('/api/v3/users', {
      params: {
        with_ids: Array.from(new Set(userIds)).join(','),
      },
    });

    const users = response.data.map(normalizeUser);

    userAssessments.forEach((assessment) => {
      assessment.user = users.find((u) => u.id === assessment.userId);
    });
  }

  return {
    userAssessments: userAssessments,
    total: response.data.meta.total,
  };
};

export const fetchUserAssessment = async (
  userAssessmentId: UserAssessment['id']
): Promise<UserAssessment> => {
  const response = await api.get<any>(
    `/api/v2/user_assessments/${userAssessmentId}`
  );

  const userAssessment = normalizeUserAssessment(response.data);

  // Fetch associated assessment details
  if (userAssessment.assessmentId) {
    const assessmentResponse = await api.get<Assessment>(
      `/api/v2/assessments/${userAssessment.assessmentId}`
    );
    userAssessment.assessment = normalizeAssessment(assessmentResponse.data);
  }

  // Fetch associated user details if userId exists
  if (userAssessment.userId) {
    const userResponse = await api.get<User>(
      `/api/v3/users/${userAssessment.userId}`
    );
    userAssessment.user = normalizeUser(userResponse.data);
  }

  return userAssessment;
};

export const deleteUserAssessment = async (
  userAssessmentId: UserAssessment['id']
): Promise<UserAssessment> => {
  const response = await api.delete<UserAssessment>(
    `/api/v2/user_assessments/${userAssessmentId}`
  );

  return normalizeUserAssessment(response.data);
};

interface ExportUserAssessmentResponse {
  resource_id: string;
}

export const exportUserAssessment = async (
  userAssessmentId: UserAssessment['id']
): Promise<string> => {
  const response = await api.get<ExportUserAssessmentResponse>(
    `/api/v2/user_assessments/${userAssessmentId}/export_assessment/`
  );

  return response.data.resource_id;
};

export const attachUserAssessmentToAccount = async ({
  userAssessmentId,
  userId,
}: {
  userAssessmentId: Identifier;
  userId: Identifier;
}): Promise<UserAssessment> => {
  const response = await api.post(
    `/api/v2/user_assessments/${userAssessmentId}/attach`,
    { user_id: userId }
  );
  return normalizeUserAssessment(response.data);
};

interface AssignAssessmentPayload {
  userId: User['id'];
  itemId: string;
  itemType: 'assessment' | 'sequence';
  sendReminders: false | Timestamp;
  unassignAfterCompletion: boolean;
}

export const assignAssessment = async ({
  userId,
  itemId,
  itemType,
  sendReminders,
  unassignAfterCompletion,
}: AssignAssessmentPayload): Promise<void> => {
  const payload: Record<string, any> = {
    user_id: userId,
    assessment_reminders: [sendReminders, unassignAfterCompletion],
  };

  if (itemType === 'assessment') {
    payload.assigned_assessment = itemId;
  } else if (itemType === 'sequence') {
    payload.assigned_assessment_sequence = itemId;
  } else {
    throw new Error('Invalid item type');
  }

  const response = await api.put(
    '/api/v2/assessments/assign_assessment',
    payload
  );

  return response.data;
};

export interface AssessmentSequenceListItem {
  id: number;
  name: string;
}

export const fetchAssessmentSequenceOptions = async (): Promise<
  AssessmentSequenceListItem[]
> => {
  const response = await api.get<AssessmentSequenceListItem[]>(
    '/api/v3/groupings',
    {
      params: {
        fetch_all: true,
        groupable_type: 'Assessment',
        pluck_columns: 'groupings.id,groupings.name',
        'q[s]': 'name asc',
      },
    }
  );

  return response.data.map(normalizeAssessmentSequenceList);
};

export const fetchAssessmentSequences = async (): Promise<
  AssessmentSequence[]
> => {
  const response = await api.get<AssessmentSequence[]>('/api/v3/groupings', {
    params: {
      fetch_all: true,
      groupable_type: 'Assessment',
      'q[s]': 'name asc',
    },
  });

  return response.data.map(normalizeAssessmentSequence);
};

interface UnassignResponse {
  success: boolean;
  for_group?: boolean;
}

interface UnassignParams {
  userId: User['id'];
  itemId: Identifier;
}

export const unassignAssessment = async ({
  userId,
  itemId,
}: UnassignParams): Promise<UnassignResponse> => {
  const response = await api.get<UnassignResponse>(
    `/api/v2/assessments/${itemId}/remove_assigned/`,
    {
      params: {
        user_id: userId,
        type: 'assessment',
      },
    }
  );

  return response.data;
};

export const unassignAssessmentSequence = async ({
  userId,
  itemId,
}: UnassignParams): Promise<UnassignResponse> => {
  const response = await api.get<UnassignResponse>(
    `/api/v2/assessments/${itemId}/remove_assigned/`,
    {
      params: {
        user_id: userId,
        type: 'grouping',
      },
    }
  );

  return response.data;
};

interface FetchRemindersOptions {
  userId: User['id'];
}

export const fetchAssignmentReminders = async ({
  userId,
}: FetchRemindersOptions): Promise<AssessmentReminder[]> => {
  const response = await api.get('/api/v2/assessments/reminder_assessments', {
    params: { user_id: userId },
  });

  // Normalize the response
  return response.data.map((reminder) => ({
    id: reminder.id,
    type: reminder.type,
    unassignAfterCompletion: reminder.unassign_after_completion,
    reminder: reminder.reminder,
  }));
};

export const importAssessmentSequence = async (
  params: AssessmentImportParams
): Promise<void> => {
  await api.post(
    `/api/v2/assessments/${params.sequenceId}/copy_to_calendar`,
    params
  );
};

export const bulkImportAssessmentSequence = async (
  params: AssessmentImportParams
): Promise<void> => {
  await api.get(
    `/api/v2/assessments/${params.sequenceId}/bulk_copy_to_calendar`,
    {
      params: {
        ...decamelizeKeys({
          ...params,
          startDate: format(new Date(params.startDate), 'MM/dd/yyyy'),
        }),
      },
    }
  );
};

// Fetch all user assessments without pagination
export const fetchAllUserAssessments = async ({
  userId,
}: {
  userId: User['id'];
}): Promise<UserAssessment[]> => {
  const response = await api.get<FetchUserAssessmentsResponse>(
    '/api/v2/user_assessments',
    {
      params: {
        user_id: userId,
        fetch_all: true,
      },
    }
  );

  return response.data.assessment.map(normalizeUserAssessment);
};

// Fetch all platform assessments
export const fetchAllAssessments = async (): Promise<Assessment[]> => {
  const response = await api.get<Assessment[]>('/api/v2/assessments', {
    params: {
      fetch_all: true,
    },
  });

  return response.data.map(normalizeAssessment);
};

// Fetch all platform assessment sequences
export const fetchAllAssessmentSequences = async (): Promise<
  AssessmentSequence[]
> => {
  const response = await api.get<AssessmentSequence[]>('/api/v3/groupings', {
    params: {
      fetch_all: true,
      groupable_type: 'Assessment',
    },
  });

  return response.data.map(normalizeAssessmentSequence);
};

// Fetch assessments with pagination and search for trainer
interface FetchAssessmentsOptions {
  page?: number;
  perPage?: number;
  search?: string;
}

interface FetchAssessmentsResponse {
  assessments: Assessment[];
  total: number;
}

export const fetchAssessments = async ({
  page = 1,
  perPage = 20,
  search = '',
}: FetchAssessmentsOptions): Promise<FetchAssessmentsResponse> => {
  const response = await api.get('/api/v2/assessments', {
    params: {
      page,
      per: perPage,
      'q[name_cont]': search,
    },
  });

  return {
    assessments: response.data?.assessment?.map(normalizeAssessment),
    total: response.data?.meta?.total,
  };
};

// Fetch a single assessment by ID
export const fetchAssessment = async (
  assessmentId: Assessment['id']
): Promise<Assessment> => {
  const response = await api.get(`/api/v2/assessments/${assessmentId}`);
  return normalizeAssessment(response.data);
};

// Delete an assessment
interface DeleteAssessmentOptions {
  assessmentId: Assessment['id'];
}

export const deleteAssessment = async ({
  assessmentId,
}: DeleteAssessmentOptions): Promise<void> => {
  await api.delete(`/api/v2/assessments/${assessmentId}`);
};

// Clone an assessment
interface CloneAssessmentOptions {
  assessmentId: Assessment['id'];
}

export const cloneAssessment = async ({
  assessmentId,
}: CloneAssessmentOptions): Promise<Assessment> => {
  const response = await api.get(`/api/v2/assessments/${assessmentId}/clone`);
  return normalizeAssessment(response.data);
};

// Create an assessment
interface CreateAssessmentOptions {
  data: Partial<Assessment>;
}

export const createAssessment = async ({
  data,
}: CreateAssessmentOptions): Promise<Assessment> => {
  const payload = decamelizeKeys(data, { deep: true });
  const response = await api.post(`/api/v2/assessments`, {
    assessment: payload,
  });

  return normalizeAssessment(response.data);
};

export const updateAssessment = async ({
  assessmentId,
  data,
}: {
  assessmentId: Assessment['id'];
  data: Partial<Assessment>;
}): Promise<Assessment> => {
  const payload = denormalizeAssessment(data as Assessment);
  const response = await api.put(`/api/v2/assessments/${assessmentId}`, {
    assessment: payload,
  });
  return normalizeAssessment(response.data);
};

// Get automation count for an assessment
interface GetAutomationCountOptions {
  assessmentId: Assessment['id'];
}

interface AutomationCountResponse {
  count: number;
}

export const getAssessmentAutomationCount = async ({
  assessmentId,
}: GetAutomationCountOptions): Promise<number> => {
  const response = await api.get<AutomationCountResponse>(
    `/api/v2/assessments/${assessmentId}/automation_count/`
  );
  return response.data.count;
};

export const fetchAssessmentSequencesWithPagination = async ({
  page = 1,
  perPage = 18,
}: {
  page?: number;
  perPage?: number;
}): Promise<{
  sequences: AssessmentSequence[];
  total: number;
}> => {
  const response = await api.get('/api/v3/groupings', {
    params: {
      page,
      per: perPage,
    },
  });

  return {
    sequences: response.data?.grouping?.map(normalizeAssessmentSequence) || [],
    total: response.data?.meta?.total || 0,
  };
};

export const createAssessmentSequence = async ({
  data,
}: { data: Partial<AssessmentSequence> }): Promise<AssessmentSequence> => {
  const payload = decamelizeKeys(data, { deep: true });
  const response = await api.post('/api/v3/groupings', {
    grouping: payload,
  });
  return normalizeAssessmentSequence(response.data);
};

export const updateAssessmentSequence = async ({
  sequenceId,
  data,
}: {
  sequenceId: AssessmentSequence['id'],
  data: Partial<AssessmentSequence>
}): Promise<AssessmentSequence> => {
  const payload = decamelizeKeys(data, { deep: true });
  const response = await api.put(`/api/v3/groupings/${sequenceId}`, {
    grouping: payload,
  });
  return normalizeAssessmentSequence(response.data);
};
export const deleteAssessmentSequence = async ({
  sequenceId,
}: { sequenceId: AssessmentSequence['id'] }): Promise<void> => {
  await api.delete(`/api/v3/groupings/${sequenceId}`);
};
export const deleteGroupItem = async (groupItemId: GroupingItem['id']): Promise<AssessmentSequence> => {
  const response = await api.delete(`/api/v3/grouping_items/${groupItemId}`);
  return response.data;
};
